// lib/drugs/class-axes.ts
//
// The `asset_classes.axis` vocabulary, in one place, for the two drug
// composers (`load-drugs-overview.ts`, `load-drug-detail.ts`) and the drawer.
// BE-CLASS-1 Phase 5, 2026-09-07. A LEAF: imports nothing.
//
// Six axes are live. Three come from openFDA (`epc`, `moa`, `cs`), one each
// from MeSH pharmacological actions (`pa`, BE-CLASS-1 Phase 2), Open Targets
// mechanisms (`mech`, Phase 3) and the EMA medicines report's ATC group
// (`atc`, Phase 4). The order below is the display order everywhere a list
// of axes is printed; `CLASS_LINE_AXES` is the subset the one-line card class
// may draw from, in preference order. `atc` never reaches the card line: a
// level-2 therapeutic group ("Drugs used in diabetes") is not a class of the
// drug, and `cs` (chemical structure) stays off it as before.

export type ClassAxis = "epc" | "moa" | "pa" | "mech" | "cs" | "atc";

/** Display order for any list of axes. */
export const CLASS_AXIS_ORDER: readonly ClassAxis[] = ["epc", "moa", "pa", "mech", "cs", "atc"];

/** The axes the card's one-line class may draw from, first hit wins. Never `cs`, never `atc`. */
export const CLASS_LINE_AXES: readonly ClassAxis[] = ["epc", "moa", "pa", "mech"];

/** Human label per axis, source in brackets. Unknown axes fall back to the raw code (`classAxisLabel`). */
export const CLASS_AXIS_LABELS: Readonly<Record<ClassAxis, string>> = {
  epc: "Established class (FDA)",
  moa: "Mechanism of action (FDA)",
  cs: "Chemical structure (FDA)",
  pa: "Pharmacological action (MeSH)",
  mech: "Mechanism (Open Targets)",
  atc: "ATC therapeutic group (EMA)",
};

/** Muted suffix the drawer prints beside an `atc` line. The group is the regulator's, not ours. */
export const ATC_ATTRIBUTION = "ATC group from the EMA medicines report";

export function isClassAxis(axis: string | null | undefined): axis is ClassAxis {
  return typeof axis === "string" && (CLASS_AXIS_ORDER as readonly string[]).includes(axis);
}

/** Position in `CLASS_AXIS_ORDER`; unknown axes sort after every known one. */
export function classAxisRank(axis: string | null | undefined): number {
  const i = isClassAxis(axis) ? CLASS_AXIS_ORDER.indexOf(axis) : -1;
  return i === -1 ? CLASS_AXIS_ORDER.length : i;
}

/** The label for an axis, or the raw code when the axis is not one of the six. */
export function classAxisLabel(axis: string): string {
  return isClassAxis(axis) ? CLASS_AXIS_LABELS[axis] : axis;
}

/**
 * `asset_classes.confidence` is TEXT ('high' | 'medium' | 'low'). Ranked so
 * "highest confidence first" is never a string compare; null or unknown = 0.
 */
export const CLASS_CONFIDENCE_RANK: Readonly<Record<string, number>> = { high: 3, medium: 2, low: 1 };

export function classConfidenceRank(confidence: string | null | undefined): number {
  if (!confidence) return 0;
  return CLASS_CONFIDENCE_RANK[confidence.trim().toLowerCase()] ?? 0;
}

/** The fields the within-axis order needs; both composers' class rows carry them. */
export type ClassOrderFields = { confidence: string | null; class_norm: string | null; class_name: string | null };

/**
 * Within one axis: higher `confidence` first, then `class_norm` alphabetically
 * (the lower-cased trimmed name when the column is null). Never read order:
 * `asset_classes.id` is a random uuid, so the row a `.order(["id"])` read
 * returns first is arbitrary (BE-CLASS-1 Phase 5 review fix 1).
 */
export function compareClassRows(a: ClassOrderFields, b: ClassOrderFields): number {
  const byConfidence = classConfidenceRank(b.confidence) - classConfidenceRank(a.confidence);
  if (byConfidence !== 0) return byConfidence;
  return classNormOf(a).localeCompare(classNormOf(b));
}

function classNormOf(r: ClassOrderFields): string {
  if (r.class_norm && r.class_norm.trim().length > 0) return r.class_norm.trim();
  return (r.class_name ?? "").trim().toLowerCase();
}
